import { OrgBaseInterface } from "../Shared/OrgBase.Interace";
import { UserVarKey } from "../Variables/Generated/UserVarKey.Data";
import { BotSequenceType } from "./BotSequence.Enums";

// LINK libs/entities/src/BotSequences/BotSequenceEnrollment.Entity.ts

/**
 * Membership lifecycle of an enrollment. Suppression is `suppressUntil`, not a status.
 * Open membership == ACTIVE; a foreseen FUTURE membership is not a row here at all —
 * it lives as a scheduled outbox request (`runAfter` = the crossing date) that wakes
 * the reconciler to re-derive on the due date.
 */
export enum BotSequenceEnrollmentStatus {
  ACTIVE = "ACTIVE",
  CLOSED = "CLOSED"
}

/** R2 boundary: the reconciler owns SEQUENCE-origin rows and ignores CAMPAIGN-origin ones. */
export enum BotSequenceEnrollmentOrigin {
  SEQUENCE = "SEQUENCE",
  CAMPAIGN = "CAMPAIGN"
}

/**
 * Universal core + per-type values, stamped on every close.
 * Per v1.1.0: `opted_out` is NOT a closure (opt-out suppresses at the send-time gate),
 * and `max_steps_reached` is a handler concern, not a reconciler close.
 */
export enum BotSequenceClosureReason {
  // universal core
  DELETED = "deleted",
  MANUAL_UNENROLL = "manual_unenroll",
  SUPERSEDED = "superseded",
  ERROR = "error",
  IDENTITY_UNLINKED = "identity_unlinked",
  /**
   * The owning user is soft-deleted: every open row of that user, lease- and user-anchored
   * alike, closes with this reason by the user's own run and the rules do not run (ENG-3518).
   */
  USER_DELETED = "user_deleted",
  // per-type (pilot types)
  PAID = "paid",
  /**
   * Past-due: the tenant's FMS-synced disablePastDue flag was set — WORLD data (a fact
   * about the tenant, not location configuration), so it ends membership like any
   * other world change.
   */
  TENANT_DISABLE_PAST_DUE = "tenant_disable_past_due",
  /**
   * Past-due: the due date moved but the lease is STILL delinquent — usually a partial
   * payment, but any due-date movement qualifies (an FMS data correction, a timezone
   * change re-reducing paidThroughDate), hence the neutral name (renamed from
   * partial_payment, 2026-07-23). The old episode closes and a fresh enrollment (new
   * anchor, new ladder position) is created in the same transaction — anchors are
   * immutable, episodes explicit.
   */
  DUE_DATE_MOVED = "due_date_moved",
  LEASE_ENDED = "lease_ended",
  RESERVATION_COMPLETED = "reservation_completed",
  LEAD_CONVERTED = "lead_converted",
  /** Move-out: the scheduled move-out was cancelled (moveOutDate cleared). */
  MOVE_OUT_CANCELLED = "moveout_cancelled"
}

/** The nine invocation paths + backfill + the handler's inline fire-pipeline run, carried through to the audit trail. */
export enum BotSequenceReconcileSource {
  FMS_SYNC = "fms-sync",
  FMS_WEBHOOK = "fms-webhook",
  BOT_EVENT = "bot-event",
  MANUAL = "manual",
  /** A wake the reconciler planted for itself: the calendar crossing (due date) arrived. */
  SCHEDULED = "scheduled",
  TICKET_APPROVAL = "ticket-approval",
  USER_MERGE = "user-merge",
  /** The user was soft-deleted (`UserAggregateService.deleteUser`) — the run closes everything `user_deleted` (ENG-3518). */
  USER_DELETED = "user-deleted",
  THIRD_PARTY = "third-party",
  BACKFILL = "backfill",
  /** The handler's pre-send inline reconcile — re-derives membership from just-JIT-refreshed data. */
  FIRE_PIPELINE = "fire-pipeline"
}

/**
 * The facts membership was derived from, frozen at decision time.
 * Audit + cheap idempotence only — never render context (messages render from
 * fresh JIT data at fire time).
 *
 * `asOf` = a UTC INSTANT (full ISO timestamp, never a calendar date): the moment the
 * FMS data this decision derived from was fetched — the location's last successful
 * sync (`location_sync_result.lastSyncedAt`). Stored unreduced; when a calendar
 * interpretation is needed (PastDueRule's time-staleness test: "what would the
 * reduction have said at the last sync?") the instant is reduced in the location's
 * timezone at use time. Deliberately NOT a send-time freshness signal — the fire
 * pipeline's JIT skip reads the live entity cache, never the frozen anchor.
 */
export interface BotSequencePastDueAnchor {
  pastDueSince: string;
  paidThroughDate: string | null;
  balance: number | null;
  /**
   * The lease's raw FMS status, verbatim (SSMCloud: "Overlock"/"Auction"/…). The
   * normalized copy on the row (rawLeaseStatus) is the query key; a PROD-559
   * gate-classification change is anchor drift (superseded).
   */
  rawStatus: string | null;
  /** FMS-side ids of the records the decision derived from — lets an auditor look them up in the FMS directly. */
  externalLeaseId: string | null;
  externalTenantId: string | null;
  externalUnitId: string | null;
  /** FMS-side location/facility id (the FMSId location variable). */
  externalLocationId: string | null;
  asOf: string | null;
}

export interface BotSequenceAbandonedCartAnchor {
  cartDate: string;
  /**
   * The cart lead's exact FMS-side creation instant (leadCreatedDate, ISO) — frozen at
   * decision time for sub-day scheduling (e.g. a "+30 minutes since creation" step) and
   * audit. The LIVE instant is always reachable via enrollment.leadId → lead.leadCreatedDate.
   */
  cartCreatedAt: string;
  leadStatus: string | null;
  /** FMS-side id of the lead the cart derived from — lets an auditor look it up in the FMS directly. */
  externalLeadId: string | null;
  /** FMS-side location/facility id (the FMSId location variable). */
  externalLocationId: string | null;
  asOf: string | null;
}

export interface BotSequenceMoveInAnchor {
  /** The move-in day as a local calendar date (ISO) — Day 0 of the ladder. */
  moveInDate: string;
  externalLeaseId: string | null;
  externalTenantId: string | null;
  externalUnitId: string | null;
  /** FMS-side location/facility id (the FMSId location variable). */
  externalLocationId: string | null;
  asOf: string | null;
}

export interface BotSequenceMoveOutAnchor {
  /** The (scheduled or actual) move-out day as a local calendar date (ISO) — Day 0 of the ladder. */
  moveOutDate: string;
  externalLeaseId: string | null;
  externalTenantId: string | null;
  externalUnitId: string | null;
  /** FMS-side location/facility id (the FMSId location variable). */
  externalLocationId: string | null;
  asOf: string | null;
}

export type BotSequenceEnrollmentAnchor =
  | BotSequencePastDueAnchor
  | BotSequenceAbandonedCartAnchor
  | BotSequenceMoveInAnchor
  | BotSequenceMoveOutAnchor;

export interface BotSequenceEnrollmentInterface extends OrgBaseInterface {
  locationId: number | null;
  userId: number;
  /** Set for lease-anchored types (past-due); null for user-anchored (abandoned-cart). C-18 uniqueness key. */
  leaseId: number | null;
  /** The lease's unit (lease-anchored types); null for user-anchored. */
  unitId: number | null;
  /** The user's tenant at the enrollment's location — informational. Always null for ABANDONED_CART (a cart is a lead-side fact; the lead links via leadId). */
  tenantId: number | null;
  /** The cart's lead row — set only for ABANDONED_CART; null for lease-anchored types. */
  leadId: number | null;
  /** The location's FMS integration id (the fms table row, from the FMSIntegrationId variable). */
  fmsId: number | null;
  sequenceType: BotSequenceType;
  origin: BotSequenceEnrollmentOrigin;
  status: BotSequenceEnrollmentStatus;
  closureReason: BotSequenceClosureReason | null;
  /** Self-expiring hold; compared, never reset. Written only by the reconciler. */
  suppressUntil: Date | null;
  /** Times applySuppression touched this row (commitment-change audit counter, ticket-approval flows). */
  commitmentChangeCount: number;
  /**
   * Day 0 of the ladder — the date the enrollment becomes applicable to any sequence
   * ladder, as an ISO calendar date (`YYYY-MM-DD`) interpreted at the location's midnight
   * at read time. Duplicates the anchor's decision date (pastDueSince / cartDate /
   * moveInDate / moveOutDate) as a typed, indexable column; write-once per episode.
   */
  anchorDate: string;
  /**
   * The lease's raw FMS status at episode creation, normalized (lowercased, trimmed) —
   * the scheduler's indexed PROD-559 exclusion key (SSMCloud overlock/auction). Stamped
   * for PAST_DUE rows only; the FMS-verbatim value lives in anchor.rawStatus. Frozen per
   * episode: a gate-relevant change is anchor drift (close + fresh row), never an update.
   */
  rawLeaseStatus: string | null;
  /** The send path's fire record (handler-written); progress derivation reads this. */
  lastFiredAt: Date | null;
  /**
   * Informational step cursor — the furthest ladder step fired, handler-written in the
   * fire transaction (scheduling-flow design). For admin/user display ("what comes
   * next"); due-ness derives from anchorDate + lastFiredAt, not from this.
   */
  furthestStepFired: number | null;
  /** Version of the rule function that wrote this row (drives scoped backfills). */
  ruleVersion: number;
  anchor: BotSequenceEnrollmentAnchor;
}

/** Per-type decision summary recorded on the audit row. */
export interface BotSequenceReconcileDecision {
  sequenceType: BotSequenceType;
  created: number;
  closed: number;
  unchanged: number;
  /** Enrollment ids this run created/closed — the run ↔ enrollment audit linkage. */
  createdIds: number[];
  closedIds: number[];
  /**
   * Subset of `closedIds` that belonged to ANOTHER user: open rows on leases this run's user
   * now owns, closed `identity_unlinked` before the user's own creates (ENG-3505). Absent on
   * runs that closed no foreign rows.
   */
  closedForeignIds?: number[];
}

/** Outcome of one reconcile run; `failed` rows are written outside the rolled-back transaction (ENG-3505 C-3). */
export enum BotSequenceReconcileRunStatus {
  SUCCEEDED = "succeeded",
  FAILED = "failed"
}

export interface BotSequenceReconcileRunInterface extends OrgBaseInterface {
  userId: number;
  source: BotSequenceReconcileSource;
  changedFields: string[] | null;
  created: number;
  closed: number;
  suppressed: number;
  unchanged: number;
  durationMs: number;
  lockWaitMs: number;
  /**
   * The next calendar crossing this run planted as a scheduled outbox wake
   * (runAfter of the request the settle wrote); null when nothing was scheduled.
   */
  scheduledCrossingAt: Date | null;
  decisions: BotSequenceReconcileDecision[] | null;
  status: BotSequenceReconcileRunStatus;
  /** The thrown error's message (bounded) plus the Postgres code when present; null on success. */
  error: string | null;
}

// LINK libs/entities/src/BotSequences/BotSequenceReconcileRequest.Entity.ts
/** The transactional outbox row — one per (orgId, userId); the backlog is capped at one row per user. */
export interface BotSequenceReconcileRequestInterface extends OrgBaseInterface {
  userId: number;
  sources: BotSequenceReconcileSource[];
  changedFields: string[];
  firstRequestedAt: Date;
  /** The sliding debounce deadline; drained when <= now(). */
  runAfter: Date;
  /** Claim stamp set by the drainer; doubles as the claim token for the conditional delete. */
  claimedAt: Date | null;
}

export enum BotSequenceBackfillRunStatus {
  RUNNING = "RUNNING",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
  CANCELLED = "CANCELLED"
}

export enum BotSequenceBackfillReason {
  ORG_FLAGGED = "org-flagged",
  RULE_VERSION = "rule-version",
  MANUAL = "manual"
}

export interface BotSequenceBackfillRunInterface extends OrgBaseInterface {
  reason: BotSequenceBackfillReason;
  locationIds: number[] | null;
  sequenceTypes: BotSequenceType[] | null;
  status: BotSequenceBackfillRunStatus;
  /** Keyset cursor (last processed userId per selector) — makes the run resumable. */
  cursor: BotSequenceBackfillCursor | null;
  usersProcessed: number;
  usersDeferred: number;
  error: string | null;
}

export interface BotSequenceBackfillCursor {
  /** Selector currently being walked (one per pilot sequence type). */
  selector: BotSequenceType;
  lastUserId: number;
}

/** Which entity a watched field lives on, as seen by the sync/capture seam. */
export enum BotSequenceWatchedEntity {
  LEASE = "lease",
  TENANT = "tenant",
  LEAD = "lead",
  USER_VARIABLE = "user_variable"
}

export type BotSequenceWatchList = Partial<Record<BotSequenceWatchedEntity, readonly string[]>>;

/**
 * Per sequence type: the fields membership derives from. Only changes to these wake
 * the reconciler. Deliberately NOT lease.daysPastDue (increments daily — would wake the
 * whole delinquent population every sync; per-FMS unreliable). isPastDue is derived
 * from paidThroughDate vs. today.
 */
export const BOT_SEQUENCE_WATCH_LISTS: Partial<Record<BotSequenceType, BotSequenceWatchList>> = {
  [BotSequenceType.PAST_DUE]: {
    // tenancyActive (ENG-3168) is the membership key — the tenant still holds the unit whatever
    // the delinquency stage; `active` also encodes online payability for SSMCloud/SiteLink and is
    // not a PAST_DUE input. disablePastDue is FMS-synced WORLD data (a tenant fact, not location
    // config) — a membership input, so its changes wake the reconciler.
    // userId (ENG-3505 C-6): a lease re-pointed to another user wakes the NEW owner too, so its run
    // closes the old owner's row `identity_unlinked` and enrolls itself; the previous owner's wake stays.
    [BotSequenceWatchedEntity.LEASE]: ["paidThroughDate", "paymentStatus", "balance", "tenancyActive", "deletedDate", "userId"],
    [BotSequenceWatchedEntity.TENANT]: ["disablePastDue", "userId"]
  },
  [BotSequenceType.ABANDONED_CART]: {
    // Cart membership derives from the candidate LEADS (leadCreatedDate picks the
    // current cart). Deliberately NOT lead.raw: it churns every sync — a StorEdge
    // cart closing via raw.closed is picked up by the variable writer's wake or any
    // other wake for the user (holistic recompute), at worst a little late.
    [BotSequenceWatchedEntity.LEAD]: ["status", "userId", "leadCreatedDate"],
    [BotSequenceWatchedEntity.USER_VARIABLE]: [UserVarKey.AbandonedCartDate]
  },
  [BotSequenceType.MOVE_IN]: {
    // tenancyActive (ENG-3168), same as PAST_DUE — MoveInRule keys membership on it, not on `active`.
    [BotSequenceWatchedEntity.LEASE]: ["moveInDate", "tenancyActive", "deletedDate", "userId"]
  },
  [BotSequenceType.MOVE_OUT]: {
    // No `active`: a moved-out lease is typically inactive and stays a member through the window.
    [BotSequenceWatchedEntity.LEASE]: ["moveOutDate", "deletedDate", "userId"]
  }
};

/** Union of every implemented type's watched fields for one entity — what the capture seam diffs against. */
export function getBotSequenceWatchedFields(entity: BotSequenceWatchedEntity): readonly string[] {
  const fields: Set<string> = new Set();
  for (const watchList of Object.values(BOT_SEQUENCE_WATCH_LISTS)) {
    for (const field of watchList[entity] ?? []) {
      fields.add(field);
    }
  }
  return [...fields];
}
